import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'
import type { ExchangeLevel } from './use-exchange-messages'

interface UnreadCountParams {
  level: ExchangeLevel
  teamId?: string
  featureId?: string
  userStoryId?: string
}

async function fetchUnreadCount(params: UnreadCountParams): Promise<number> {
  const { data, error } = await supabase.rpc('get_unread_message_count', {
    p_level: params.level,
    p_team_id: params.teamId || null,
    p_feature_id: params.featureId || null,
    p_user_story_id: params.userStoryId || null,
  })

  if (error) {
    console.error('Error fetching unread count:', error)
    return 0
  }

  return data ?? 0
}

async function markAsRead(params: UnreadCountParams): Promise<void> {
  const { error } = await supabase.rpc('mark_exchange_read', {
    p_level: params.level,
    p_team_id: params.teamId || null,
    p_feature_id: params.featureId || null,
    p_user_story_id: params.userStoryId || null,
  })

  if (error) {
    console.error('Error marking as read:', error)
    throw new Error(error.message)
  }
}

export function useUnreadCount(
  level: ExchangeLevel,
  scopeId: string | undefined
) {
  const params: UnreadCountParams = {
    level,
    ...(level === 'team' && { teamId: scopeId }),
    ...(level === 'feature' && { featureId: scopeId }),
    ...(level === 'user_story' && { userStoryId: scopeId }),
  }

  return useQuery({
    queryKey: ['unreadCount', level, scopeId],
    queryFn: () => fetchUnreadCount(params),
    enabled: !!scopeId,
    refetchInterval: 30000, // Poll every 30s as backup to Realtime
  })
}

export function useMarkAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: { level: ExchangeLevel; scopeId: string }) => {
      const markParams: UnreadCountParams = {
        level: params.level,
        ...(params.level === 'team' && { teamId: params.scopeId }),
        ...(params.level === 'feature' && { featureId: params.scopeId }),
        ...(params.level === 'user_story' && { userStoryId: params.scopeId }),
      }
      return markAsRead(markParams)
    },
    onSuccess: (_, variables) => {
      // Invalidate the unread count query to reset the badge
      queryClient.invalidateQueries({
        queryKey: ['unreadCount', variables.level, variables.scopeId],
      })
    },
  })
}
